import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Loader2, Eye, MessageCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportToCsv } from "@/lib/csv-export";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface OrderItem {
  id: string;
  product_name: string;
  size: string | null;
  quantity: number;
  unit_price: number;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  shipping_address: string | null;
  shipping_city: string | null;
  shipping_fee: number;
  promo_code: string | null;
  discount_percentage: number;
  discount_amount: number;
  status: string;
  total_amount: number;
  notes: string | null;
  created_at: string;
  order_items: OrderItem[];
}

const statusOptions = [
  { value: "pending", label: "Pending", color: "bg-yellow-500/10 text-yellow-500" },
  { value: "confirmed", label: "Confirmed", color: "bg-blue-500/10 text-blue-500" },
  { value: "processing", label: "Processing", color: "bg-purple-500/10 text-purple-500" },
  { value: "shipped", label: "Shipped", color: "bg-indigo-500/10 text-indigo-500" },
  { value: "delivered", label: "Delivered", color: "bg-green-500/10 text-green-500" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-500/10 text-red-500" },
];

const getWhatsAppMessage = (order: Order, status: string) => {
  const itemsList = order.order_items
    .map(item => `• ${item.product_name}${item.size ? ` (${item.size})` : ''} × ${item.quantity} — ${item.unit_price * item.quantity} EGP`)
    .join('\n');

  const messages: Record<string, { text: string; label: string }> = {
    pending: {
      label: "Confirm Order",
      text: `السلام عليكم ${order.customer_name} 👋\n\nشكراً لطلبك من *بَيْتُ الطِّيبِ* ✨\n\nتفاصيل الطلب #${order.id.slice(0, 8)}:\n${itemsList}\n\n💰 الإجمالي: ${order.total_amount} EGP\n🚚 الشحن إلى: ${order.shipping_city || 'غير محدد'}\n\nنود تأكيد طلبك. هل البيانات صحيحة؟ ✅`,
    },
    confirmed: {
      label: "Notify Confirmed",
      text: `السلام عليكم ${order.customer_name} 👋\n\nتم تأكيد طلبك #${order.id.slice(0, 8)} بنجاح ✅\n\nجاري تجهيز طلبك وسيتم إرساله في أقرب وقت.\n\nشكراً لثقتك في *بَيْتُ الطِّيبِ* 🌸`,
    },
    processing: {
      label: "Notify Processing",
      text: `السلام عليكم ${order.customer_name} 👋\n\nطلبك #${order.id.slice(0, 8)} قيد التجهيز الآن 📦\n\nسنوافيك بتفاصيل الشحن فور إرسال الطلب.\n\nشكراً لصبرك 🌹`,
    },
    shipped: {
      label: "Notify Shipped",
      text: `السلام عليكم ${order.customer_name} 👋\n\nتم شحن طلبك #${order.id.slice(0, 8)} 🚚✨\n\n📍 الشحن إلى: ${order.shipping_city || 'غير محدد'}\n📦 العنوان: ${order.shipping_address || 'غير محدد'}\n\nسيصلك الطلب قريباً إن شاء الله.\n\nشكراً لتسوقك من *بَيْتُ الطِّيبِ* 🌸`,
    },
    delivered: {
      label: "Confirm Delivery",
      text: `السلام عليكم ${order.customer_name} 👋\n\nنأمل أن طلبك #${order.id.slice(0, 8)} وصلك بخير 🎉\n\nنتمنى أن تنال منتجاتنا إعجابك ✨\n\nلو عندك أي استفسار أو ملاحظة لا تتردد في التواصل معنا.\n\nشكراً لثقتك في *بَيْتُ الطِّيبِ* 💛`,
    },
    cancelled: {
      label: "Notify Cancelled",
      text: `السلام عليكم ${order.customer_name} 👋\n\nللأسف تم إلغاء الطلب #${order.id.slice(0, 8)} ❌\n\nلو حابب تعيد الطلب أو عندك أي استفسار، تواصل معنا في أي وقت.\n\n*بَيْتُ الطِّيبِ* 🌹`,
    },
  };

  return messages[status] || messages.pending;
};

const AdminOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          order_items (*)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast({
        title: "Error",
        description: "Failed to load orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("orders")
        .update({ status: newStatus })
        .eq("id", orderId);

      if (error) throw error;

      setOrders(orders.map((order) =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      toast({ title: "Success", description: "Order status updated" });
    } catch (error: any) {
      console.error("Error updating order:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update order",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this order?")) return;

    try {
      const { error } = await supabase.from("orders").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Order deleted successfully" });
      fetchOrders();
    } catch (error: any) {
      console.error("Error deleting order:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete order",
        variant: "destructive",
      });
    }
  };

  const viewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsDialogOpen(true);
  };

  const getStatusStyle = (status: string) => {
    return statusOptions.find((s) => s.value === status)?.color || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleExportOrders = () => {
    exportToCsv(
      `orders-${format(new Date(), "yyyy-MM-dd")}`,
      ["Order ID", "Customer", "Email", "Phone", "City", "Address", "Status", "Promo Code", "Discount %", "Shipping Fee", "Total (EGP)", "Date", "Items"],
      orders.map((o) => [
        o.id.slice(0, 8),
        o.customer_name,
        o.customer_email,
        o.customer_phone,
        o.shipping_city,
        o.shipping_address,
        o.status,
        o.promo_code,
        o.discount_percentage,
        o.shipping_fee,
        o.total_amount,
        format(new Date(o.created_at), "yyyy-MM-dd HH:mm"),
        o.order_items.map((i) => `${i.product_name}${i.size ? ` (${i.size})` : ""} x${i.quantity}`).join("; "),
      ])
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-serif">Orders</h1>
        <Button variant="outline" size="sm" onClick={handleExportOrders} disabled={orders.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        <AnimatePresence>
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-card border border-border rounded-sm p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-sm text-muted-foreground">#{order.id.slice(0, 8)}</p>
                  <p className="font-medium">{order.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                </div>
                <p className="font-medium">{order.total_amount} EGP</p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {format(new Date(order.created_at), "MMM d, yyyy")}
                </span>
                <Select
                  value={order.status}
                  onValueChange={(value) => updateStatus(order.id, value)}
                >
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusStyle(order.status)}`}>
                        {statusOptions.find((s) => s.value === order.status)?.label || order.status}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => viewOrder(order)}>
                  <Eye className="w-4 h-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(order.id)}
                  className="text-destructive hover:text-destructive/80"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {orders.length === 0 && (
          <div className="p-8 text-center text-muted-foreground bg-card border border-border rounded-sm">
            No orders yet.
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Order ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Total</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {orders.map((order) => (
                  <motion.tr
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-4 font-mono text-sm">
                      #{order.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-muted-foreground">
                      {format(new Date(order.created_at), "MMM d, yyyy")}
                    </td>
                    <td className="px-4 py-4">{order.total_amount} EGP</td>
                    <td className="px-4 py-4">
                      <Select
                        value={order.status}
                        onValueChange={(value) => updateStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusStyle(order.status)}`}>
                              {statusOptions.find((s) => s.value === order.status)?.label || order.status}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              <span className={`px-2 py-1 text-xs rounded-full ${status.color}`}>
                                {status.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => viewOrder(order)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(order.id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>

          {orders.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No orders yet. Orders will appear here when customers make purchases.
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              Order #{selectedOrder?.id.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium break-all">{selectedOrder.customer_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedOrder.customer_phone || "N/A"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {format(new Date(selectedOrder.created_at), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
              </div>

              {selectedOrder.shipping_address && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Shipping Address</p>
                  <p className="font-medium">{selectedOrder.shipping_address}</p>
                </div>
              )}

              <div className="border-t border-border pt-4">
                <p className="text-sm text-muted-foreground mb-2">Items</p>
                <div className="space-y-2">
                  {selectedOrder.order_items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="flex-1">
                        {item.product_name} {item.size && `(${item.size})`} × {item.quantity}
                      </span>
                      <span className="ml-4">{item.unit_price * item.quantity} EGP</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-4 space-y-2 text-sm">
                {(() => {
                  const itemsTotal = selectedOrder.order_items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
                  return (
                    <>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Subtotal</span>
                        <span>{itemsTotal.toFixed(2)} EGP</span>
                      </div>
                      {selectedOrder.promo_code && (
                        <div className="flex justify-between text-primary">
                          <span>Discount ({selectedOrder.promo_code} - {selectedOrder.discount_percentage}%)</span>
                          <span>-{(selectedOrder.discount_amount || 0).toFixed(2)} EGP</span>
                        </div>
                      )}
                      <div className="flex justify-between text-muted-foreground">
                        <span>Shipping ({selectedOrder.shipping_city || "N/A"})</span>
                        <span>{(selectedOrder.shipping_fee || 0).toFixed(2)} EGP</span>
                      </div>
                      <div className="flex justify-between font-medium text-base pt-2 border-t border-border">
                        <span>Total</span>
                        <span>{selectedOrder.total_amount} EGP</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {selectedOrder.notes && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Notes</p>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}

              {selectedOrder.customer_phone && (
                <div className="pt-2">
                  <Button
                    className="w-full bg-[#25D366] hover:bg-[#1da851] text-white"
                    onClick={() => {
                      const { text } = getWhatsAppMessage(selectedOrder, selectedOrder.status);
                      const phone = selectedOrder.customer_phone!.replace(/^0/, '20');
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {getWhatsAppMessage(selectedOrder, selectedOrder.status).label} via WhatsApp
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
